import React, { ReactElement, useCallback, useState } from "react";
import Avatar from "react-avatar";
import {
	Button,
	Col,
	Container,
	Form,
	FormControl,
	Modal,
	Row,
} from "react-bootstrap";
import {
	ProjectTask,
	ProjectTaskActionList,
	ProjectTaskAddChecklistForm,
	ProjectTaskChecklistList,
	ProjectTaskCommentForm,
	ProjectTaskCommentList,
	ProjectTaskDueDate,
	ProjectTaskLabelsList,
	ProjectTaskLabelsSelect,
	ProjectTaskTimelogForm,
	useProjects,
} from ".";
import { AttachmentsListGroup } from "../attachments";
import { OpportunitesForAccountSelect } from "../opportunities";
import { Editable, Loading } from "../ui";
import { UserSelect, useUsers } from "../users";

import {
	faAlarmClock,
	faAlignLeft,
	faCalendar,
	faClock,
	faComment,
	faCreditCardBlank,
	faDollarSign,
	faEye,
	faLink,
	faListUl,
	faPaperclip,
	faTag,
	faTasks,
	faUser,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import Clipboard from "react-clipboard.js";
import DatePicker from "react-datepicker";
import { useDropzone } from "react-dropzone";
import ReactMarkdown from "react-markdown";
import { toast } from "react-toastify";

interface IParams {
	task: ProjectTask;
	show: boolean;
	onHide: () => void;
}

export const ProjectTaskModal = (props: IParams): ReactElement => {
	const [isEditingMembers, setIsEditMembers] = useState(false);

	const projectContext = useProjects();
	const userContext = useUsers();

	const task = props.task;

	if (!task) {
		return <Loading />;
	}

	const onDrop = useCallback(acceptedFiles => {
		if (task.id) {
			projectContext.uploadFiles!(acceptedFiles, task);
		}
	}, []);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
	});

	const memberAvatars = task.members.map((member, index) => (
		<Avatar
			round
			key={index}
			name={member.user ? member.user.fullName : "NA"}
			size="30"
			color="#dfe1e6"
			fgColor="#172b4d"
			textSizeRatio={2}
			style={{ marginRight: 8 }}
		/>
	));

	const handleClickWatch = async (event: React.MouseEvent) => {
		event.stopPropagation();
		const newTask = await projectContext.toggleWatchingTask!(task);

		if (userContext.user) {
			if (newTask.isWatchedBy(userContext.user)) {
				toast(
					"You are now watching this task, you will be notified when comments or changes are made.",
				);
			} else {
				toast("You are no longer watching this task.");
			}
		}
	};

	const avatarEdit = (
		<Editable
			isEditing={isEditingMembers}
			shownComponent={
				<>
					{memberAvatars}
					<Avatar
						round
						name="+"
						size="30"
						color="#dfe1e6"
						fgColor="#172b4d"
						textSizeRatio={2}
					/>
				</>
			}
			editComponent={
				<Form.Group>
					<UserSelect
						defaultValue={task.members.map(member => {
							if (member.user) {
								return {
									value: member.user.id,
									label: member.user.fullName,
								};
							}
							return { 1: member.id };
						})}
						handleChange={(_selected, type) => {
							let option = null;
							if (type.action === "remove-value") {
								option = type.removedValue;
							} else {
								option = type.option;
							}

							projectContext.saveTaskMembers!(
								task,
								type.action,
								option.value,
							);
						}}
					/>
				</Form.Group>
			}
			onFinishedEditing={() => {
				setIsEditMembers(false);
			}}
		/>
	);

	const nameEdit = (
		<Editable
			shownComponent={
				<>
					<FontAwesomeIcon
						icon={faCreditCardBlank}
						className="task-icon icon-card-blank"
						size="lg"
						fixedWidth
					/>
					<h1>
						#{task.id} - {task.name}
						{userContext.user ? (
							<FontAwesomeIcon
								className={
									task.isWatchedBy(userContext.user)
										? "active"
										: ""
								}
								icon={faEye}
								onClick={handleClickWatch}
								fixedWidth
							/>
						) : (
							""
						)}
					</h1>
				</>
			}
			editComponent={
				<FormControl
					type="text"
					className="task-name-input"
					defaultValue={task.name}
					onChange={(event: any) => {
						task.name = event.target.value;
					}}
				/>
			}
			onFinishedEditing={() => {
				projectContext.saveTask!(task);
			}}
		/>
	);

	const descriptionEdit = (
		<Editable
			shownComponent={
				<div className="task-description">
					{task.description.length ? (
						<ReactMarkdown source={task.description} />
					) : (
						"Enter a description..."
					)}
				</div>
			}
			editComponent={
				<FormControl
					as="textarea"
					rows={20}
					defaultValue={task.description}
					onChange={(event: any) => {
						task.description = event.target.value;
					}}
				/>
			}
			onFinishedEditing={() => {
				projectContext.saveTask!(task);
			}}
		/>
	);

	const timelogForm = (
		<Editable
			shownComponent={
				<Button className="button-link btn-block mb-3" block>
					<FontAwesomeIcon icon={faClock} size="1x" fixedWidth />
					Log Time
				</Button>
			}
			editComponent={<ProjectTaskTimelogForm task={task} />}
			onFinishedEditing={() => {
				return;
			}}
		/>
	);

	const addChecklistForm = (
		<Editable
			shownComponent={
				<Button className="button-link btn-block mb-3" block>
					<FontAwesomeIcon icon={faTasks} size="1x" fixedWidth />
					Add Checklist
				</Button>
			}
			editComponent={<ProjectTaskAddChecklistForm task={task} />}
			onFinishedEditing={() => {
				return;
			}}
		/>
	);

	const dueDateForm = (
		<Editable
			shownComponent={
				<Button className="button-link btn-block mb-3" block>
					<FontAwesomeIcon icon={faAlarmClock} size="1x" fixedWidth />
					Due Date
				</Button>
			}
			editComponent={
				<DatePicker
					showTimeInput
					className="form-control mb-3"
					selected={task.dueDate}
					onChange={(date: Date) => {
						task.dueDate = date;
						projectContext.saveTask!(task);
					}}
				/>
			}
			onFinishedEditing={() => {
				return;
			}}
		/>
	);

	const startDateForm = (
		<Editable
			shownComponent={
				<Button className="button-link btn-block mb-3" block>
					<FontAwesomeIcon icon={faCalendar} size="1x" fixedWidth />
					Start Date
				</Button>
			}
			editComponent={
				<DatePicker
					showTimeInput
					className="form-control mb-3"
					selected={task.startDate}
					onChange={(date: Date) => {
						task.startDate = date;
						projectContext.saveTask!(task);
					}}
				/>
			}
			onFinishedEditing={() => {
				return;
			}}
		/>
	);

	const estimatedHoursForm = (
		<Editable
			shownComponent={
				<Button className="button-link btn-block mb-3" block>
					<FontAwesomeIcon icon={faClock} size="1x" fixedWidth />
					Time allocated
				</Button>
			}
			editComponent={
				<FormControl
					type="number"
					defaultValue={task.estimatedHours}
					onChange={(event: any) => {
						task.estimatedHours = event.target.value;
						projectContext.saveTask!(task);
					}}
				/>
			}
			onFinishedEditing={() => {
				return;
			}}
		/>
	);

	const linkOpportunityForm = (
		<Editable
			shownComponent={
				<Button className="button-link btn-block mb-3" block>
					<FontAwesomeIcon icon={faDollarSign} size="1x" fixedWidth />
					Link Opportunity
				</Button>
			}
			editComponent={
				<OpportunitesForAccountSelect
					accountId={
						projectContext.project
							? String(projectContext.project.accountId)
							: "0"
					}
					handleChange={(selected, _type) => {
						task.opportunityId = selected.value;
						projectContext.saveTask!(task);
					}}
					defaultValue={
						task.opportunity
							? {
									value: task.opportunity.id,
									label: task.opportunity.name,
							  }
							: null
					}
				/>
			}
			onFinishedEditing={() => {
				return;
			}}
		/>
	);

	const labelsField = (
		<Editable
			shownComponent={
				<Button className="button-link btn-block mb-3" block>
					<FontAwesomeIcon icon={faTag} size="1x" fixedWidth />
					Labels
				</Button>
			}
			editComponent={
				<ProjectTaskLabelsSelect
					handleChange={(selected, _type) => {
						task.labels = selected;
						projectContext.saveTask!(task);
					}}
					defaultValue={task.labels.map(label => {
						return {
							value: label.id,
							label: label.name,
						};
					})}
				/>
			}
			onFinishedEditing={() => {
				return;
			}}
		/>
	);

	let dueDate = null;
	if (task.dueDate) {
		dueDate = (
			<>
				<span className="list-info mt-4 mb-2">DUE DATE</span>
				<ProjectTaskDueDate
					task={task}
					dateFormat="MMM D [at] h:mm A"
				/>
			</>
		);
	}

	let startDate = null;
	if (task.startDate) {
		startDate = (
			<>
				<span className="list-info mt-4 mb-2">START DATE</span>
				<div>
					<FontAwesomeIcon
						icon={faCalendar}
						size="1x"
						fixedWidth
						style={{ marginRight: 4 }}
					/>
					{moment(task.startDate).format("MMM D [at] h:mm A")}
				</div>
			</>
		);
	}

	return (
		<Modal
			show={props.show}
			onHide={props.onHide}
			size="lg"
			className="task-modal"
		>
			<div
				{...getRootProps({
					onClick: event => event.stopPropagation(),
				})}
				style={{ height: "100%", outline: "0" }}
			>
				<Modal.Body>
					<Container>
						<Row>
							<Col sm={9}>
								<div className="task-modal-title">
									{nameEdit}
									<span className="list-info">
										In list <u>{task.status}</u>
										<br />
										{task.estimatedHours
											? `Estimated to take ${
													task.estimatedHours
											  } hours to complete`
											: ""}
									</span>

									<Row>
										<Col>
											<span className="list-info mt-4 mb-2">
												MEMBERS
											</span>
											{avatarEdit}
											{task.labels.length ? (
												<>
													<span className="list-info mt-4 mb-2">
														LABELS
													</span>
													<ProjectTaskLabelsList
														task={task}
													/>
												</>
											) : (
												""
											)}
										</Col>
										<Col>
											{startDate}
											{dueDate}
										</Col>
									</Row>
								</div>

								<div>
									<FontAwesomeIcon
										className="task-icon"
										icon={faAlignLeft}
										size="lg"
										fixedWidth
									/>
									<h3>Description</h3>
								</div>

								{descriptionEdit}

								{task.attachments.length ? (
									<div className="mt-4">
										<FontAwesomeIcon
											className="task-icon"
											icon={faPaperclip}
											size="lg"
											fixedWidth
										/>
										<h3>Attachments</h3>
										<AttachmentsListGroup
											attachments={task.attachments}
											folder={`projects/${
												task.projectId
											}/tasks/${task.id}/`}
										/>
									</div>
								) : (
									""
								)}

								{task.checklists.length > 0 ? (
									<ProjectTaskChecklistList task={task} />
								) : null}

								<div className="mt-4">
									<FontAwesomeIcon
										className="task-icon"
										icon={faComment}
										size="lg"
										fixedWidth
									/>
									<h3>Comments</h3>
								</div>
								<ProjectTaskCommentList task={task} />
								<ProjectTaskCommentForm task={task} />
								<div className="mt-4">
									<FontAwesomeIcon
										className="task-icon"
										icon={faListUl}
										size="lg"
										fixedWidth
									/>
									<h3>Activity</h3>
								</div>
								<ProjectTaskActionList actions={task.actions} />
							</Col>
							<Col sm={3} style={{ paddingTop: 35 }}>
								<span className="list-info mb-2">Actions</span>
								<div className="task-actions">
									<Button
										className="button-link mt-3 mb-3"
										block
										onClick={() => setIsEditMembers(true)}
									>
										<FontAwesomeIcon
											icon={faUser}
											size="1x"
											fixedWidth
										/>
										Members
									</Button>{" "}
									{labelsField}
									{dueDateForm}
									{startDateForm}
									{linkOpportunityForm}
									{estimatedHoursForm}
									{timelogForm}
									{addChecklistForm}
									<Clipboard
										className="button-link btn btn-primary btn-block mb-3"
										data-clipboard-text={`${
											window.location.origin
										}/projects/${task.projectId}/${
											task.id
										}`}
										onSuccess={() =>
											toast.success(
												"Link copied to clipboard.",
											)
										}
									>
										<FontAwesomeIcon
											icon={faLink}
											size="1x"
											fixedWidth
										/>
										Copy Link
									</Clipboard>
								</div>
							</Col>
						</Row>
					</Container>
				</Modal.Body>
				<input {...getInputProps()} />
				{isDragActive ? (
					<div className="modal-overlay">Drop the files here...</div>
				) : (
					""
				)}
			</div>
		</Modal>
	);
};
